import { useEffect, useState } from 'react'
import { useQuestionsUpdate } from '../api/hooks/Questions/useQuestionsUpdate'
import { useVideosUpdate } from '../api/hooks/Videos/useVideosUpdate'
import { useQuizStore } from '../store'

const initialCurriculumData = {
  level: '',
  mainChapter: '',
  subChapter: '',
  unit: '',
  detailUnit: '',
  count: 1,
  questionLabel: '',
  selectedType: 'A',
}

export const useLectureVideoData = (data) => {
  const { initializeFromData } = useQuizStore()
  const [curriculum, setCurriculum] = useState(initialCurriculumData)

  const questionsUpdateMutation = useQuestionsUpdate()
  const videosUpdateMutation = useVideosUpdate()

  useEffect(() => {
    if (data) {
      initializeFromData(data)
    }
  }, [data, initializeFromData])

  const handleLevelChange = async (value) => {
    if (value) {
      updateCurriculum('level', value)
      await updateCurriculumField({ LEVEL: value })
    }
  }

  const handleMainChapterChange = async (value) => {
    updateCurriculum('mainChapter', value)
    await updateCurriculumField({ MAIN: value })
  }

  const handleSubChapterChange = async (value) => {
    updateCurriculum('subChapter', value)
    await updateCurriculumField({ SUB: value })
  }

  const handleUnitChange = async (value) => {
    updateCurriculum('unit', value)
    await updateCurriculumField({ UNIT: value })
  }

  const handleDetailUnitChange = async (value) => {
    updateCurriculum('detailUnit', value)
    await updateCurriculumField({ DETAIL_UNIT: value })
  }

  const handleCountChange = async (value) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    const number = parseInt(numericValue) || 0

    if (numericValue === '' || (number >= 1 && number <= 100)) {
      updateCurriculum('no', number)
      if (number > 0) {
        await updateCurriculumField({ NO: number })
      }
    }
  }

  const handleTypeChange = async (value) => {
    if (value) {
      updateCurriculum('selectedType', value)
      await updateCurriculumField({ TYPE: value })
    }
  }

  const updateCurriculumField = async (updatedFields) => {
    try {
      if (!data || typeof data.QUESTION_ID !== 'number') return

      const updateData = {
        QUESTION_ID: data.QUESTION_ID,
        LEVEL: curriculum.level || data.LEVEL?.toString() || '',
        MAIN: curriculum.mainChapter || data.MAIN?.toString() || '',
        SUB: curriculum.subChapter || data.SUB?.toString() || '',
        UNIT: curriculum.unit || data.UNIT?.toString() || '',
        DETAIL_UNIT:
          curriculum.detailUnit || data.DETAIL_UNIT?.toString() || '',
        DEPTH5: curriculum.count || data.DEPTH5 || 0,
        TITLE: data.TITLE || '',
        IMG_URL: data.IMG_URL || '',
        ANSWER1: data.ANSWER1 || '',
        ANSWER2: data.ANSWER2 || '',
        ANSWER3: data.ANSWER3 || '',
        ANSWER4: data.ANSWER4 || '',
        ANSWER5: data.ANSWER5 || '',
        IS_CORRECT_1: data.IS_CORRECT_1 ?? false,
        IS_CORRECT_2: data.IS_CORRECT_2 ?? false,
        IS_CORRECT_3: data.IS_CORRECT_3 ?? false,
        IS_CORRECT_4: data.IS_CORRECT_4 ?? false,
        IS_CORRECT_5: data.IS_CORRECT_5 ?? false,
        DESC: data.DESC || '',
        DESC_URL: data.DESC_URL || '',
        ...updatedFields,
      }

      await questionsUpdateMutation.mutateAsync(updateData)
    } catch (error) {
      console.error('updateCurriculumField 호출 실패:', error)
    }
  }

  const handleSaveLecture = async (videoInfo) => {
    try {
      if (!data || typeof data.QUESTION_ID !== 'number') {
        throw new Error('유효하지 않은 비디오 데이터입니다.')
      }

      const updateData = {
        VIDEO_ID: data.QUESTION_ID?.toString() || '',
        VIMEO_ID: videoInfo.vimeoId?.toString() || '',
        LEVEL: curriculum.level || data.LEVEL?.toString() || '',
        MAIN: curriculum.mainChapter || data.MAIN?.toString() || '',
        SUB: curriculum.subChapter || data.SUB?.toString() || '',
        UNIT: curriculum.unit || data.UNIT?.toString() || '',
        DETAIL_UNIT:
          curriculum.detailUnit || data.DETAIL_UNIT?.toString() || '',
        TITLE: videoInfo.title || '',
        HASHKEY: videoInfo.hashKey || '',
      }

      await videosUpdateMutation.mutateAsync(updateData)
    } catch (error) {
      console.error('강의 Save Failed:', error)

      if (error instanceof Error && 'response' in error) {
        const axiosError = error
        console.error('Error response:', axiosError.response?.data)
        console.error('Error status:', axiosError.response?.status)
        console.error('Error config:', axiosError.config)
      }

      throw error
    }
  }

  return {
    curriculum,
    isLoading:
      questionsUpdateMutation.isPending || videosUpdateMutation.isPending,
    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    handleCountChange,
    handleTypeChange,
    handleSaveLecture,
  }
}
